/**
 * Targets and manual effects of a spell or ability on the chain (tiers 1-2; owner report 2026-10-08:
 * "When playing a spell I need to be able to select a target card, whether friendly or not, and modify it
 * based on the spell").
 *
 * - `target` chooses the targets of your item stacked by hand (CR 355.7: targets are chosen as it is
 *   played). They are stored as the item's `rules.targets`, so every player sees them as arrows and the
 *   rules knowledge layer reads them like the targets of an advisor play ("a spell that chooses …").
 *   Targets are public objects or your own (CR 355.10.a: choices in hidden zones are not targets, and
 *   another player's hidden objects can't be named, ADR-0008). An item played through the advisor keeps
 *   the targets it chose while playing (one that chose none, a "manual" card, takes them by hand).
 * - `modify` applies one change to one of those targets by hand: the manual resolution of its effect
 *   (CR 359.3), logged with its source. It is the only way to change another player's objects in tiers
 *   1-2 (02 §4.1: otherwise you act only on what you control), and only while your item is on the chain.
 * - "This turn" marks (`mightUp` / `mightDown` counters, stuns until the end of turn) end with the turn
 *   (CR 317.2.c, 424.1.a.2): `clearThisTurnMarks`.
 */
import { type Rejection, reject } from '../../kernel/errors';
import { relocate } from '../core/objects';
import { findBattlefield, findObject } from '../core/queries';
import { type ObjRef } from '../events/types';
import { refOf } from '../events/refs';
import type { ChainItem, ChainItemRules } from '../state/chain';
import type { GameState } from '../state/game-state';
import type { ObjectId } from '../state/ids';
import type { GameObject } from '../state/objects';
import { type ZoneRef, sameZone } from '../state/zones';
import { type CommandContext, emit, visibleObject } from './context';
import type { SpellChange, TableCommand } from './types';

type Target = Extract<TableCommand, { t: 'target' }>;
type Modify = Extract<TableCommand, { t: 'modify' }>;

/** The counters of "± Might this turn" (net: only one of them is ever set). */
export const MIGHT_UP = 'mightUp';
export const MIGHT_DOWN = 'mightDown';

/** Most targets one item can have. */
export const MAX_TARGETS = 8;

/** Largest amount of one change (damage, Might, buffs). */
export const MAX_CHANGE = 99;

/** The actor's item on the chain. */
function ownItem(ctx: CommandContext, id: string): ChainItem | Rejection {
  const item = ctx.draft.chain.find((i) => i.id === id);
  if (!item) return reject('unknownChainItem', id);
  if (item.controller !== ctx.actor) return reject('notYours', id);
  return item;
}

/** The item's source as an event reference (its card on the chain, or the ability's source if it still exists). */
function sourceRef(ctx: CommandContext, item: ChainItem): ObjRef | null {
  const source = findObject(ctx.draft, item.object ?? item.source);
  return source ? refOf(ctx.draft, source) : null;
}

/** The objects an item targets now (every instruction's). */
export function itemTargets(item: Pick<ChainItem, 'rules'>): ObjectId[] {
  return [...new Set((item.rules?.targets ?? []).flatMap((t) => t.objects))];
}

/** Whether the actor may choose `o` as a target: a public object, or one of their own in a hidden zone. */
function targetable(ctx: CommandContext, o: GameObject): boolean {
  const kind = o.zone.kind;
  const hidden = kind === 'hand' || kind === 'mainDeck' || kind === 'runeDeck' || kind === 'facedown';
  return !hidden || o.owner === ctx.actor || o.controller === ctx.actor;
}

/** Rules data for an item stacked by hand: only its targets (it paid nothing the rules recorded). */
function manualRules(targets: ObjectId[]): ChainItemRules {
  return {
    ability: null,
    mode: [],
    targets: [{ instruction: 0, objects: targets }],
    paid: {},
    xValue: null,
    openedChain: false,
    openedBy: null,
    triggerData: null,
    sourceSnapshot: null,
  };
}

/** Chooses (or clears) the targets of the actor's item on the chain. */
export function handleTarget(ctx: CommandContext, cmd: Target): Rejection | undefined {
  const item = ownItem(ctx, cmd.item);
  if ('rejected' in item) return item;
  // An advisor play that chose targets keeps them (CR 355.7); one that chose none (a card without a
  // ruleset entry, "manual", Q21) gets them by hand like a card stacked by hand.
  if (!item.manual && itemTargets(item).length > 0)
    return reject('notPermitted', 'Its targets were chosen as it was played (CR 355.7)');
  if (cmd.targets.length > MAX_TARGETS) return reject('outOfRange', `At most ${MAX_TARGETS} targets`);
  if (new Set(cmd.targets).size !== cmd.targets.length) return reject('invalidCommand', 'A target is listed twice');
  const objects: GameObject[] = [];
  for (const id of cmd.targets) {
    const o = visibleObject(ctx, id);
    if ('rejected' in o) return o;
    if (o.id === item.object) return reject('invalidCommand', "A spell can't choose itself (CR 355.9.c)");
    if (o.category === 'battlefield' && o.zone.kind !== 'battlefield') return reject('invalidCommand', id);
    if (!targetable(ctx, o)) return reject('notPermitted', 'Only public objects or your own can be chosen');
    objects.push(o);
  }
  const current = itemTargets(item);
  if (current.length === cmd.targets.length && current.every((t, i) => t === cmd.targets[i])) {
    return reject('noChange');
  }
  if (cmd.targets.length === 0) {
    // Rules data made only for the targets goes away with them.
    if (item.rules && Object.keys(item.rules.paid).length === 0) item.rules = null;
    else if (item.rules) item.rules = { ...item.rules, targets: [] };
  } else if (item.rules === null) item.rules = manualRules(cmd.targets.slice());
  else item.rules = { ...item.rules, targets: [{ instruction: 0, objects: cmd.targets.slice() }] };
  emit(ctx, {
    type: 'targeted',
    actor: ctx.actor,
    item: item.id,
    source: sourceRef(ctx, item),
    targets: objects.map((o) => refOf(ctx.draft, o)),
  });
  return undefined;
}

/** Whether a change applies to an object where it is (a permanent on the board; a spell for counter). */
function changeFits(change: SpellChange, o: GameObject, item: ChainItem): Rejection | undefined {
  const z = o.zone.kind;
  const onBoard = z === 'base' || z === 'battlefield';
  switch (change.kind) {
    case 'counter':
      if (z !== 'chain' || o.id === item.object) return reject('invalidCommand', 'Counter a spell on the chain');
      return undefined;
    case 'exhaust':
    case 'ready':
      if (onBoard || z === 'legend' || z === 'runes') return undefined;
      return reject('invalidCommand', 'Exhaust or ready something on the board');
    default:
      if (o.category !== 'card') return reject('invalidCommand', 'Only units and gear can be changed that way');
      if (!onBoard) return reject('invalidCommand', 'It is not on the board');
      return undefined;
  }
}

function amount(n: number): Rejection | undefined {
  if (!Number.isInteger(n) || n === 0 || Math.abs(n) > MAX_CHANGE)
    return reject('outOfRange', `The amount must be a non-zero integer up to ±${MAX_CHANGE}`);
  return undefined;
}

/** Moves a target for its item's controller (it keeps its own controller on the board). */
function moveTarget(ctx: CommandContext, o: GameObject, to: ZoneRef): void {
  const from = o.zone;
  const before = refOf(ctx.draft, o);
  const moved = relocate(ctx.draft, o.id, to, { controller: o.controller });
  emit(ctx, {
    type: 'moved',
    actor: ctx.actor,
    from,
    to,
    object: before,
    result: moved.after ? refOf(ctx.draft, moved.after) : null,
    position: null,
  });
}

/** Sets the net "± Might this turn" of an object. */
function addMight(o: GameObject, n: number): void {
  const net = (o.counters[MIGHT_UP] ?? 0) - (o.counters[MIGHT_DOWN] ?? 0) + n;
  Reflect.deleteProperty(o.counters, MIGHT_UP);
  Reflect.deleteProperty(o.counters, MIGHT_DOWN);
  if (net > 0) o.counters[MIGHT_UP] = net;
  if (net < 0) o.counters[MIGHT_DOWN] = -net;
}

/** Applies one change to a target of the actor's item on the chain. */
export function handleModify(ctx: CommandContext, cmd: Modify): Rejection | undefined {
  const item = ownItem(ctx, cmd.item);
  if ('rejected' in item) return item;
  const o = visibleObject(ctx, cmd.target);
  if ('rejected' in o) return o;
  if (!itemTargets(item).includes(o.id)) return reject('notPermitted', 'Choose it as a target of that item first');
  const change = cmd.change;
  const bad = changeFits(change, o, item);
  if (bad) return bad;
  const target = refOf(ctx.draft, o);
  const source = sourceRef(ctx, item);
  switch (change.kind) {
    case 'damage': {
      const n = amount(change.n);
      if (n) return n;
      const value = Math.max(0, o.damage + change.n);
      if (value === o.damage) return reject('noChange', 'It has no damage to heal');
      o.damage = value;
      break;
    }
    case 'might': {
      const n = amount(change.n);
      if (n) return n;
      addMight(o, change.n);
      break;
    }
    case 'buff': {
      const n = amount(change.n);
      if (n) return n;
      const value = Math.max(0, o.buffs + change.n);
      if (value === o.buffs) return reject('noChange', 'It has no buff to remove');
      o.buffs = value;
      break;
    }
    case 'stun': {
      const stunned = o.statuses.some((s) => s.kind === 'stunned');
      if (stunned === change.on) return reject('noChange');
      if (change.on) o.statuses.push({ kind: 'stunned', source: item.object ?? item.source, until: 'endOfTurn' });
      else o.statuses = o.statuses.filter((s) => s.kind !== 'stunned');
      break;
    }
    case 'exhaust':
    case 'ready': {
      const exhausted = change.kind === 'exhaust';
      if (o.exhausted === exhausted) return reject('noChange');
      o.exhausted = exhausted;
      break;
    }
    case 'kill':
    case 'banish':
    case 'returnToHand':
    case 'counter': {
      const kind = change.kind === 'banish' ? 'banishment' : change.kind === 'returnToHand' ? 'hand' : 'trash';
      emit(ctx, { type: 'modified', actor: ctx.actor, item: item.id, source, target, change });
      moveTarget(ctx, o, { kind, player: o.owner });
      return undefined;
    }
    case 'move': {
      let to: ZoneRef;
      if (change.to === 'base') to = { kind: 'base', player: o.controller };
      else {
        if (!findBattlefield(ctx.draft, change.to)) return reject('unknownBattlefield', change.to);
        to = { kind: 'battlefield', battlefield: change.to };
      }
      if (sameZone(o.zone, to)) return reject('noChange', 'It is already there');
      emit(ctx, { type: 'modified', actor: ctx.actor, item: item.id, source, target, change });
      moveTarget(ctx, o, to);
      return undefined;
    }
  }
  emit(ctx, { type: 'modified', actor: ctx.actor, item: item.id, source, target, change });
  return undefined;
}

/** Adds `delta(id)` to the "± Might this turn" marks of every card on the board (tier-1 "Apply effect"). */
export function addMightMarks(draft: GameState, delta: (id: ObjectId) => number): void {
  for (const o of Object.values(draft.objects)) {
    if (o.category !== 'card' || (o.zone.kind !== 'base' && o.zone.kind !== 'battlefield')) continue;
    const n = delta(o.id);
    if (n !== 0) addMight(o, n);
  }
}

/**
 * Ends the "this turn" marks of `modify` (CR 317.2.c): the `mightUp` / `mightDown` counters always, and
 * stuns that last until the end of turn when `statuses` (CR 424.1.a.2; tier 2 ends those with its seat
 * setting `autoHeal`).
 */
export function clearThisTurnMarks(draft: GameState, options: { statuses: boolean }): void {
  for (const o of Object.values(draft.objects)) {
    if (MIGHT_UP in o.counters) Reflect.deleteProperty(o.counters, MIGHT_UP);
    if (MIGHT_DOWN in o.counters) Reflect.deleteProperty(o.counters, MIGHT_DOWN);
    if (options.statuses && o.statuses.some((s) => s.until === 'endOfTurn'))
      o.statuses = o.statuses.filter((s) => s.until !== 'endOfTurn');
  }
}
